import * as XLSX from 'xlsx';
import { logInfo, logWarn, logError } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'excelQuoteParser';

/** Set to true to log every supplier ID normalization (noisy). */
const ENABLE_SUPPLIER_DEBUG = false;
/** Requisition numbers to log per-row supplier IDs and full breakdown when inconsistent. */
const DEBUG_REQUISITIONS = ['3000283682', '3000283697'];

/**
 * Parses Excel requisition report files into quote payloads for the create-from-Excel flow.
 * Column header aliases aligned with backend create_from_excel / extract_rows_from_requisition_report.
 * Backend uses Hebrew headers: "דרישת רכש", "ספק קבוע", "מק\"ט ספק", "כמות", "הער.מחיר", "מטבע", etc.
 * `budget` is the הזמנה פנימית cell only; תאור הזמנה is recognized but not sent.
 * First match wins (exact Hebrew first, then English fallbacks).
 */
const COLUMN_ALIASES = {
  requisition_number: ['דרישת רכש', 'requisition number', 'requisition_number', 'order number', 'demand ref'],
  supplier_id: ['ספק קבוע', 'supplier id', 'supplier_id', 'ספק', 'inner supplier id', 'supplier'],
  supplier_name_text: ['טקסט[ספק קבוע]', 'טקסט (ספק קבוע)', 'טקסט ספק קבוע'],
  budget: ['הזמנה פנימית', 'budget', 'internal order'],
  budget_name: ['תאור הזמנה', 'budget name', 'order description'],
  request_date: ['תא.דרישה', 'request date', 'date'],
  catalog_number: ['מק"ט ספק', 'catalog number', 'catalog_number', 'supplier catalog number', 'supplier catalog', 'cat num', 'catalog'],
  product_name: ['טקסט קצר', 'product name', 'short text', 'description', 'product description'],
  unit: ['יח', 'unit', 'יח\''],
  quantity: ['כמות', 'quantity', 'quantities', 'qty'],
  price: ['הער.מחיר', 'price', 'unit price', 'unit_price', 'מחיר', 'price per unit'],
  currency: ['מטבע', 'currency', 'curr'],
};

/**
 * Normalize header cell for matching: string, trimmed, lowercased.
 * @param {*} cell
 * @returns {string}
 */
function normalizeHeader(cell) {
  if (cell == null) return '';
  return String(cell).trim().toLowerCase();
}

/**
 * True if the header is the catalog-number column (מק"ט ספק), so we don't assign it to supplier_id.
 * The short alias "ספק" would otherwise match "מק\"ט ספק" and cause catalog numbers to be read as supplier IDs.
 */
function isCatalogNumberHeader(normalized) {
  if (!normalized || normalized.length < 3) return false;
  return normalized.includes('מק"ט') || normalized.includes('מק\u201cט');
}

/**
 * Find column index for a field by matching against COLUMN_ALIASES.
 * Exact match always wins. For partial match we require header length >= 3 so short
 * headers like "N" don't match "item number" or "product name".
 * supplier_id must not match the catalog column (מק"ט ספק).
 * @param {string[]} headers - Row of header strings (normalized via normalizeHeader)
 * @returns {Object} Map of field name -> column index (0-based)
 */
function findColumnIndices(headers) {
  const indices = {};
  for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
    let idx;
    if (field === 'supplier_name_text') {
      idx = headers.findIndex(h => {
        const n = (h || '').trim();
        return n.length >= 3 && n.includes('טקסט') && n.includes('ספק קבוע');
      });
      if (idx === -1) {
        idx = headers.findIndex(h => {
          const normalized = (h || '').trim();
          return aliases.some(a => {
            const alias = (a || '').trim().toLowerCase();
            return normalized === alias;
          });
        });
      }
    } else {
      idx = headers.findIndex(h => {
        const normalized = (h || '').trim();
        if (field === 'supplier_id' && isCatalogNumberHeader(normalized)) return false;
        return aliases.some(a => {
          const alias = (a || '').trim().toLowerCase();
          if (normalized === alias) return true;
          if (normalized.length < 3) return false;
          return normalized.includes(alias) || alias.includes(normalized);
        });
      });
    }
    if (idx !== -1) indices[field] = idx;
  }
  return indices;
}

/**
 * Parse one sheet into rows of raw data (first row = headers).
 * @param {XLSX.WorkSheet} sheet
 * @returns {{ headers: string[], rows: any[][] }}
 */
function sheetToRows(sheet) {
  const data = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  });
  if (!data || data.length < 2) return { headers: [], rows: [] };
  const headers = data[0].map(c => normalizeHeader(c));
  const rows = data.slice(1).filter(row => row.some(cell => cell != null && String(cell).trim() !== ''));
  return { headers, rows };
}

/**
 * Normalize currency: ILS -> NIS to match backend.
 * @param {string} raw
 * @returns {string|null}
 */
function normalizeCurrency(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  let s = String(raw).trim().toUpperCase();
  if (s === 'ILS') s = 'NIS';
  return s || null;
}

/**
 * Build one quote payload from grouped rows (same requisition, same supplier).
 * Matches backend shape: requisition_number, supplier_id, optional budget/request_date/supplier_name_text, items.
 * `budget` is the raw הזמנה פנימית (internal-order) code only — תאור הזמנה is not appended.
 * Each item: supplier_catalog_number, quantity, unit_price, currency, product_name, unit (for product creation).
 */
function buildQuotePayload(requisition_number, supplier_id, rows, col, fileIndex) {
  const firstRow = rows[0];
  const items = rows
    .map(row => {
      const catalogNum = col.catalog_number != null ? String(row[col.catalog_number] ?? '').trim() : '';
      if (!catalogNum) return null;
      const quantity = col.quantity != null ? parseQuantity(row[col.quantity]) : 0;
      if (quantity <= 0) return null;
      const item = {
        supplier_catalog_number: catalogNum,
        quantity,
        unit_price: col.price != null ? parsePrice(row[col.price]) : null,
      };
      if (col.currency != null && row[col.currency] != null) {
        item.currency = normalizeCurrency(row[col.currency]);
      }
      if (col.product_name != null && row[col.product_name] != null) {
        const name = String(row[col.product_name]).trim();
        if (name) item.product_name = name;
      }
      if (col.unit != null && row[col.unit] != null) {
        const u = String(row[col.unit]).trim();
        if (u) item.unit = u;
      }
      return item;
    })
    .filter(Boolean);

  const payload = {
    requisition_number: String(requisition_number).trim(),
    supplier_id: supplier_id != null && String(supplier_id).trim() !== '' ? String(supplier_id).trim() : null,
    items,
    file_index: fileIndex,
  };
  if (col.supplier_name_text != null && firstRow[col.supplier_name_text] != null) {
    const t = String(firstRow[col.supplier_name_text]).trim();
    if (t) payload.supplier_name_text = t;
  }
  if (col.budget != null && firstRow[col.budget] != null) {
    const budgetCode = String(firstRow[col.budget]).trim();
    if (budgetCode) {
      payload.budget = budgetCode;
      logInfo(LOG_CONTEXT, 'Attached budget code from הזמנה פנימית', {
        requisition_number: payload.requisition_number,
        budget: budgetCode,
      });
    }
  }
  if (col.request_date != null && firstRow[col.request_date] != null) {
    const d = firstRow[col.request_date];
    if (d != null && String(d).trim() !== '') payload.request_date = String(d).trim();
  }
  return payload;
}

function parseQuantity(val) {
  if (val == null) return 0;
  const n = parseFloat(String(val).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
}

function parsePrice(val) {
  if (val == null || String(val).trim() === '') return null;
  const n = parseFloat(String(val).replace(/,/g, ''));
  return isNaN(n) ? null : n;
}

/**
 * Normalize supplier ID for consistency check: Excel may return the same value as number vs string
 * or with float artifacts (e.g. 3000283682 vs "3000283682" vs 3000283682.0000001). Collapse these
 * to one comparable value (numeric -> integer string; else trimmed string).
 * @param {*} val - Raw cell value
 * @returns {string|null} Normalized string or null if empty
 */
function normalizeSupplierIdForComparison(val) {
  if (val == null) return null;
  const s = String(val).trim();
  if (s === '') return null;
  const num = parseFloat(s.replace(/,/g, ''));
  let result;
  if (!isNaN(num) && isFinite(num)) {
    result = Number.isInteger(num) ? String(Math.round(num)) : String(num);
  } else {
    result = s;
  }
  if (ENABLE_SUPPLIER_DEBUG) {
    logInfo(LOG_CONTEXT, 'supplier id normalized', { raw: val, normalized: result });
  }
  return result;
}

/**
 * Parse a single Excel file and return quote payloads (grouped by requisition + supplier).
 * @param {File} file
 * @param {number} fileIndex
 * @returns {{ quotePayloads: Object[], errors: string[] }}
 */
function parseSingleFile(file, fileIndex) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          resolve({ quotePayloads: [], errors: [`${file.name}: No sheet found`] });
          return;
        }
        const sheet = workbook.Sheets[sheetName];
        const { headers, rows } = sheetToRows(sheet);
        const col = findColumnIndices(headers);
        logInfo(LOG_CONTEXT, `Parse file: ${file.name}`, { fileIndex, sheetName, rowCount: rows.length, columnsFound: Object.keys(col) });

        const hasRequisition = col.requisition_number != null;
        const hasSupplier = col.supplier_id != null;
        if (!hasRequisition || !hasSupplier) {
          const missing = [];
          if (!hasRequisition) missing.push('דרישת רכש (requisition number)');
          if (!hasSupplier) missing.push('ספק קבוע (supplier id)');
          logWarn(LOG_CONTEXT, `Missing required columns: ${file.name}`, { missing });
          resolve({
            quotePayloads: [],
            errors: [`${file.name}: Missing required columns: ${missing.join(', ')}.`],
          });
          return;
        }

        // Group by requisition number only (same as backend: "דרישת רכש")
        const groups = new Map();
        for (const row of rows) {
          const req = String(row[col.requisition_number] ?? '').trim();
          if (!req) continue;
          if (!groups.has(req)) groups.set(req, []);
          groups.get(req).push(row);
        }

        const quotePayloads = [];
        const errors = [];
        for (const [reqNo, groupRows] of groups) {
          const supplierIds = new Set();
          const debugRequisition = DEBUG_REQUISITIONS.includes(reqNo);
          const rowsSummary = [];
          for (let i = 0; i < groupRows.length; i++) {
            const row = groupRows[i];
            const rawSupplierCell = row[col.supplier_id];
            const normalized = normalizeSupplierIdForComparison(rawSupplierCell);
            if (normalized != null) supplierIds.add(normalized);
            if (debugRequisition) {
              rowsSummary.push({
                rowIndex: i,
                rawSupplierCell,
                normalizedSupplierId: normalized,
              });
            }
          }
          if (debugRequisition && rowsSummary.length > 0) {
            logInfo(LOG_CONTEXT, 'Supplier IDs per row (debug requisition)', {
              requisition: reqNo,
              fileName: file.name,
              groupRowCount: groupRows.length,
              rowsSummary,
            });
          }
          if (supplierIds.size === 0) {
            errors.push(`${file.name}: Requisition ${reqNo} has no supplier ID (ספק קבוע); skipped.`);
            continue;
          }
          if (supplierIds.size > 1) {
            errors.push(`${file.name}: Requisition ${reqNo} has inconsistent supplier IDs across rows; all rows must have the same supplier. Skipped.`);
            const inconsistentRowsSummary = groupRows.map((row, i) => ({
              rowIndex: i,
              rawSupplierCell: row[col.supplier_id],
              normalizedSupplierId: normalizeSupplierIdForComparison(row[col.supplier_id]),
            }));
            logWarn(LOG_CONTEXT, 'Inconsistent supplier IDs', {
              requisition: reqNo,
              fileName: file.name,
              supplierIds: [...supplierIds],
              rowsSummary: inconsistentRowsSummary,
            });
            continue;
          }
          const supplierId = [...supplierIds][0];
          const payload = buildQuotePayload(reqNo, supplierId, groupRows, col, fileIndex);
          if (payload.items.length === 0) {
            errors.push(`${file.name}: Requisition ${reqNo} has no valid items (מק"ט ספק, כמות); skipped.`);
            continue;
          }
          quotePayloads.push(payload);
        }
        logInfo(LOG_CONTEXT, `Parsed file: ${file.name}`, { quotePayloadsCount: quotePayloads.length, errorsCount: errors.length });
        if (errors.length > 0) logWarn(LOG_CONTEXT, `Parse warnings: ${file.name}`, { errors });
        resolve({ quotePayloads, errors });
      } catch (err) {
        logError(LOG_CONTEXT, `Parse error: ${file.name}`, err);
        resolve({
          quotePayloads: [],
          errors: [`${file.name}: Parse error - ${err.message}`],
        });
      }
    };
    reader.onerror = () => {
      logError(LOG_CONTEXT, `Could not read file: ${file.name}`);
      resolve({
        quotePayloads: [],
        errors: [`${file.name}: Could not read file`],
      });
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parse one or more Excel files and return combined quote payloads.
 * Used for the Excel upload review step; output is sent to backend in one request.
 *
 * @param {File[]} files - Array of Excel files (.xlsx or .xls)
 * @returns {Promise<{ success: boolean, quotePayloads: Object[], errors: string[] }>}
 */
export async function parseExcelQuoteFiles(files) {
  const fileList = Array.isArray(files) ? files : [files];
  const allPayloads = [];
  const allErrors = [];
  logInfo(LOG_CONTEXT, 'parseExcelQuoteFiles start', { fileCount: fileList.length, fileNames: fileList.map(f => f.name) });

  for (let i = 0; i < fileList.length; i++) {
    const result = await parseSingleFile(fileList[i], i);
    allPayloads.push(...result.quotePayloads);
    allErrors.push(...(result.errors || []));
  }

  logInfo(LOG_CONTEXT, 'parseExcelQuoteFiles complete', { totalPayloads: allPayloads.length, totalErrors: allErrors.length, success: allPayloads.length > 0 });
  return {
    success: allPayloads.length > 0,
    quotePayloads: allPayloads,
    errors: allErrors,
  };
}
